import {
  Box,
  Button,
  Card,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { GetApp, InfoOutlined, Save } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Page } from '../../../components';
import { FileUploader } from '../../../components/file/file-uploader';
import { ConfirmationDialogue } from '../../../components/modal/ConfirmationDialogue';
import {
  createListingStats,
  downloadImportJobErrorReport,
  getImportJob,
  IImportJob,
} from '../../../fetch/imports';
import { useToastContext } from '../../../hooks';
import { theme } from '../../../styles';

const templateUrl = `${process.env.PUBLIC_URL}/templates/Listing_Stats_Activity_Import_Template.xlsx`;

const documentationUrl = `${process.env.PUBLIC_URL}/templates/Listing_Stats_Bulk_Import_Documentation.docx`;

type ImportMode = 'Active' | 'Historical';

/** How often the queued job is polled while it runs. */
const POLL_INTERVAL_MS = 2000;

/**
 * Stop polling after this long. The job keeps running server-side; this only stops the page from
 * asking forever if the worker was lost to a restart and never reached a finished state.
 */
const POLL_TIMEOUT_MS = 30 * 60 * 1000;

export const ImportListingStats: FC = () => {
  const { showToast } = useToastContext();
  const [importName, setImportName] = useState('');
  const [importMode, setImportMode] = useState<ImportMode>('Active');
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [errorsList, setErrorsList] = useState<string[] | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [job, setJob] = useState<IImportJob | null>(null);

  const classes = useStyles();
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStartedAt = useRef<number | null>(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  // Keep polling while the background worker is running; the browser is free the whole time, which
  // is what lets files well past 10,000 rows import without a timeout or a frozen page.
  useEffect(() => {
    if (!job || job.isFinished) {
      stopPolling();
      return;
    }

    if (pollStartedAt.current === null) {
      pollStartedAt.current = Date.now();
    }

    if (Date.now() - pollStartedAt.current > POLL_TIMEOUT_MS) {
      setPollTimedOut(true);
      stopPolling();
      return;
    }

    pollTimer.current = setTimeout(async () => {
      try {
        setJob(await getImportJob(job.importJobId));
      } catch {
        showToast('error', 'Lost contact with the import. Refresh to check its status.');
        stopPolling();
      }
    }, POLL_INTERVAL_MS);

    return stopPolling;
  }, [job, showToast, stopPolling]);

  const handleSubmit = async () => {
    if (!uploadedFile || !importName) return;

    setIsSubmitting(true);
    setJob(null);
    setPollTimedOut(false);
    pollStartedAt.current = null;

    try {
      const { job: queued, errors } = await createListingStats(
        importName,
        uploadedFile,
        importMode
      );

      if (errors || !queued) {
        setErrorsList(errors);
        return;
      }

      setJob(queued);
      showToast('success', 'Import queued. Processing continues in the background.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setImportName('');
    setUploadedFile(null);
    setJob(null);
    setPollTimedOut(false);
    pollStartedAt.current = null;
  };

  const canSubmit =
    !!uploadedFile && !!importName.trim() && !isSubmitting && !(job && !job.isFinished);

  return (
    <Page title="Import Listing Stats Activities">
      <Container maxWidth={false} disableGutters>
        <Card className={classes.uploader} elevation={2}>
          <Box marginBottom="1.5rem" display="flex" alignItems="center" style={{ gap: '0.25rem' }}>
            <Button
              component="a"
              href={templateUrl}
              download
              startIcon={<GetApp />}
              color="primary"
              variant="outlined"
              size="small"
            >
              Download Template
            </Button>
            <Tooltip title="Download documentation (Word)">
              <IconButton
                component="a"
                href={documentationUrl}
                download
                size="small"
                aria-label="Download bulk import documentation"
              >
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Grid container>
            <Grid container item spacing={2} xs={12} sm={6}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  autoComplete="nope"
                  label="Import Name"
                  placeholder="Enter an import name..."
                  name="importName"
                  size="small"
                  required
                  value={importName}
                  onChange={e => setImportName(e.target.value)}
                  inputProps={{ maxLength: 300 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl>
                  <FormLabel id="listing-stats-import-mode">Import Type</FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="listing-stats-import-mode"
                    name="importMode"
                    value={importMode}
                    onChange={e => setImportMode(e.target.value as ImportMode)}
                  >
                    <FormControlLabel value="Active" control={<Radio />} label="Active" />
                    <FormControlLabel value="Historical" control={<Radio />} label="Historical" />
                  </RadioGroup>
                </FormControl>
                <Typography variant="caption" component="p" color="textSecondary">
                  {importMode === 'Active'
                    ? 'Records enter the Listing Stats pipeline and update Current and All-Time counts.'
                    : 'Records update All-Time counts only. They do not appear in the Listing Activity data grid and do not affect Current counts.'}
                </Typography>
                <Typography variant="caption" component="p" color="textSecondary">
                  Rows with no Activity Type default to &quot;No Contact / CA Not Sent&quot;, the Target
                  List type. Listing Stats shows target list activity only to employees with the HWE
                  target list flag enabled. For them these rows appear in the activity grid and count
                  toward Current and All-Time; to everyone else they stay hidden.
                </Typography>
                <Typography variant="caption" component="p" color="textSecondary">
                  Additional Contacts takes Frankie IDs separated by semicolons, for example
                  &quot;123456;789012&quot;. Commas are not accepted as separators, so that a number
                  Excel has formatted with a thousands separator is never read as two IDs. Each one
                  has to be a current contact of the row&apos;s
                  company and can appear on only one record per listing, the same rules the Listing
                  Stats screens apply. A contact that fails those checks is left off the record and
                  reported; the row still imports with the rest. It cannot be recovered by fixing the
                  file and importing again, so add it on the record in Frankie. Historical imports
                  have no activity record to hold contacts, so they ignore the column.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FileUploader
                  handleFile={val => setUploadedFile(val)}
                  handleRemove={() => setUploadedFile(null)}
                  removeText="Remove"
                  templateFile={uploadedFile?.name ?? ''}
                  isRequired
                  dropzoneText="File to Import (.xlsx)"
                />
              </Grid>
            </Grid>
          </Grid>

          <Box marginTop="2rem">
            <Button
              disabled={!canSubmit}
              onClick={handleSubmit}
              startIcon={<Save />}
              color="secondary"
              variant="contained"
            >
              Upload
            </Button>
          </Box>

          {job && (
            <Box marginTop="2rem" maxWidth="40rem">
              <Typography variant="subtitle1" gutterBottom>
                {job.isFinished
                  ? `Import ${job.status === 'Failed' ? 'failed' : 'complete'}`
                  : 'Import in progress'}
              </Typography>

              {!job.isFinished && !pollTimedOut && (
                <>
                  <LinearProgress variant="determinate" value={job.percentComplete} />
                  <Typography variant="caption" component="p" color="textSecondary">
                    {`${job.processedRows} of ${job.totalRows} row(s) processed. Job #${job.importJobId}. You can leave this page; the import keeps running.`}
                  </Typography>
                </>
              )}

              {!job.isFinished && pollTimedOut && (
                <Typography variant="body2" color="error">
                  {`Stopped checking job #${job.importJobId} after 30 minutes. It may still be running - reload the page to check again.`}
                </Typography>
              )}

              {job.status === 'Failed' && (
                <Typography variant="body2" color="error">
                  {job.failureMessage ?? 'The file could not be processed.'}
                </Typography>
              )}

              {job.isFinished && job.status !== 'Failed' && (
                <Typography variant="body2" component="div">
                  <div>{`Rows processed: ${job.processedRows}`}</div>
                  <div>{`Records imported: ${job.createdCount}`}</div>
                  <div>{`Rows skipped: ${job.skippedCount}`}</div>
                  <div>{`Rows with issues: ${job.errorCount}`}</div>
                </Typography>
              )}

              <Box marginTop="1rem" display="flex" style={{ gap: '0.5rem' }}>
                {job.isFinished && job.hasErrorReport && (
                  <Button
                    startIcon={<GetApp />}
                    color="primary"
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      try {
                        await downloadImportJobErrorReport(job.importJobId);
                      } catch {
                        showToast('error', 'Could not download the error report.');
                      }
                    }}
                  >
                    Download Error Report
                  </Button>
                )}
                {job.isFinished && (
                  <Button color="primary" variant="text" size="small" onClick={resetForm}>
                    Start Another Import
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {errorsList && (
            <ConfirmationDialogue
              open
              id="errors-listing-stats"
              title="Upload Errors"
              text={errorsList.join('\r\n')}
              hideCancel
              onClose={() => {
                setErrorsList(null);
                setUploadedFile(null);
              }}
              onConfirm={() => {
                setErrorsList(null);
                setUploadedFile(null);
              }}
            />
          )}
        </Card>
      </Container>
    </Page>
  );
};

const useStyles = makeStyles(() => ({
  uploader: {
    padding: theme.spacing(2),
  },
}));
